import { Link } from "react-router-dom";
import { T } from "@/components/T";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import RichText from "@/components/RichText";
import { SERVICE_CATEGORIES } from "@/lib/categories";
import { resolveKindImage } from "@/lib/images";
import { MandalaMotif } from "@/components/MotifBackground";
import { ArrowRight, Flame, Sparkles, Star, Calculator, Home as HomeIcon, CalendarDays, Clock } from "lucide-react";

const ICONS = { Flame, Sparkles, Star, Calculator, Home: HomeIcon, CalendarDays, Clock };

export default function ServicesOverview() {
  const { pick, lang, isDeva } = useLang();
  const { page } = useSettings();
  const P = page("/services");
  const pv = (o, d) => (pick(o?.hi, o?.en) || d);

  return (
    <>
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-16 -top-8 h-72 w-72 text-[var(--gold)] opacity-[0.07]" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline-label mb-3" data-testid="services-overline">{pv(P.subtitle, "श्रद्धा · सेवा")}</p>
          <h1 className="font-serif-latin text-4xl text-ink md:text-6xl" data-testid="services-title">{pv(P.title, "Services")}</h1>
          <p className="mt-4 max-w-2xl leading-relaxed text-ink-2" data-testid="services-explainer">
            {pv(P.explainer, "Request poojas, homams and yagyas, or a Jyotisha, numerology, vastu, panchang or muhurt consultation — offered with care and respect for tradition. No online payment; our team confirms every request personally.")}
          </p>
        </div>
      </section>
      {(P.body?.en || P.body?.hi) && (
        <section className="mx-auto max-w-7xl px-5 pt-12 md:px-8" data-testid="services-body">
          <RichText value={pick(P.body?.hi, P.body?.en)} className="reading-content max-w-none text-[17px] leading-[1.9] text-ink-2" />
        </section>
      )}

      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_CATEGORIES.map((c) => {
            const Icon = ICONS[c.icon] || Sparkles;
            const desc = lang === "en" ? c.desc_en : c.desc_hi;
            return (
              <Link key={c.kind} to={`/services/${c.kind}`} data-testid={`service-cat-${c.kind}`} className="group flex flex-col overflow-hidden border border-line bg-surface transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
                <div className="relative h-36 overflow-hidden">
                  <img src={resolveKindImage(c.kind)} alt={c.en} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgba(44,38,33,0.35)] to-transparent" />
                  <div className="absolute left-5 top-5 flex h-10 w-10 items-center justify-center bg-[var(--surface)] text-saffron"><Icon className="h-5 w-5" /></div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className={`${isDeva ? "font-deva" : "font-serif-latin"} text-2xl text-ink deva-lh`}>{pick(c.deva, c.en)}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2">{desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5"><T>Explore</T> <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}
